import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { type HonestBacktestResult } from '@/services/HonestBacktestService';
import { type ProgressiveBacktestResult } from '@/services/ProgressiveBacktestService';

interface HonestBacktestComparisonProps {
  honestResult: HonestBacktestResult;
  regularResult: ProgressiveBacktestResult;
}

export const HonestBacktestComparison: React.FC<HonestBacktestComparisonProps> = ({
  honestResult,
  regularResult
}) => {
  const accuracyDiff = honestResult.summary.accuracy - regularResult.accuracy;
  const skipRateDiff = honestResult.summary.skipRate - regularResult.skipRate;
  const predictedDiff = honestResult.summary.predictedPositions - regularResult.predictedPositions;

  const ComparisonMetric = ({ 
    label, 
    honestValue, 
    regularValue, 
    unit = '',
    inverse = false 
  }: {
    label: string;
    honestValue: number;
    regularValue: number;
    unit?: string;
    inverse?: boolean;
  }) => {
    const diff = honestValue - regularValue;
    const isPositive = inverse ? diff < 0 : diff > 0;
    const Icon = diff === 0 ? Minus : isPositive ? TrendingUp : TrendingDown;
    
    return (
      <div className="space-y-2">
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Honest</div>
            <div className="font-mono text-sm">{honestValue.toFixed(2)}{unit}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Regular</div>
            <div className="font-mono text-sm">{regularValue.toFixed(2)}{unit}</div>
          </div>
          <div className="flex items-center space-x-1">
            <Icon 
              className={`h-4 w-4 ${
                diff === 0 ? 'text-muted-foreground' :
                isPositive ? 'text-green-500' : 'text-red-500'
              }`} 
            />
            <span 
              className={`text-sm font-mono ${
                diff === 0 ? 'text-muted-foreground' :
                isPositive ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {diff > 0 ? '+' : ''}{diff.toFixed(2)}{unit}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Alert>
        <AlertDescription>
          <strong>Bias Analysis:</strong> This comparison reveals the bias in regular progressive backtesting.
          The "Honest" method rebuilds patterns at each step to simulate real-time conditions, while "Regular" 
          uses hindsight knowledge of future trades.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ComparisonMetric
              label="Accuracy"
              honestValue={honestResult.summary.accuracy}
              regularValue={regularResult.accuracy}
              unit="%"
            />
            <ComparisonMetric
              label="Skip Rate"
              honestValue={honestResult.summary.skipRate}
              regularValue={regularResult.skipRate}
              unit="%"
              inverse={true}
            />
            <ComparisonMetric
              label="Predicted Positions"
              honestValue={honestResult.summary.predictedPositions}
              regularValue={regularResult.predictedPositions}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Bias Impact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Accuracy Difference</div>
              <Badge 
                variant={accuracyDiff > 0 ? 'default' : 'destructive'}
                className="text-xs"
              >
                {accuracyDiff > 0 ? '+' : ''}{accuracyDiff.toFixed(2)}% 
                {accuracyDiff > 0 ? ' Better' : ' Worse'}
              </Badge>
              <p className="text-xs text-muted-foreground">
                {accuracyDiff > 0 ? 
                  'Honest backtest performed better, suggesting regular method may underestimate real performance.' :
                  'Regular backtest showed inflated results due to lookahead bias.'
                }
              </p>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Skip Rate Impact</div>
              <Badge 
                variant={skipRateDiff < 0 ? 'default' : 'secondary'}
                className="text-xs"
              >
                {skipRateDiff > 0 ? '+' : ''}{skipRateDiff.toFixed(2)}% 
                {skipRateDiff < 0 ? ' Less Skipping' : ' More Skipping'}
              </Badge>
              <p className="text-xs text-muted-foreground">
                {skipRateDiff > 0 ?
                  'Honest method skips more due to limited pattern history.' :
                  'Regular method appears more confident due to future knowledge.'
                }
              </p>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Reality Check</div>
              <div className="text-xs text-muted-foreground">
                The honest backtest represents what would actually happen in live trading, 
                where patterns are built incrementally without future knowledge.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};